/* colocar.js · herramienta INTERNA de MOA para colocar las piezas de un kit sobre el coche.
 *
 * El cliente no ve esto nunca: en el visor los productos salen ya colocados.
 * Aquí es donde MOA decide el sitio de cada pieza.
 *
 * NO ES UNA PÁGINA APARTE: es el visor entero (`visor/moa-visor3d.js`), con su
 * parasol, su color y su kit, más este panel. Va todo junto porque así se ve el
 * coche que verá el cliente mientras se coloca. (La primera versión era una
 * escena propia sin parasol; Mou: «lo has hecho por separado de los parasoles
 * cuando realmente va todo junto».)
 *
 * Cómo funciona: el kit es una lista de piezas pintadas sobre las «pieles» del
 * coche. Arrastrar es preguntar qué punto de qué piel hay bajo el dedo y llevar
 * la pieza allí. Hay cuatro zonas —costado, encima, delante y detrás— y la
 * pieza pasa de una a otra según hacia dónde mire la chapa que se toca.
 *
 * CADA PIEZA EXISTE DOS VECES, una por lado del coche. Se pueden mover las dos
 * a la vez (enlazadas: la derecha es el espejo de la izquierda) o solo la de un
 * lado. (Antes iban siempre las dos a la vez; Mou: «tiene que dejarte la opción
 * de elegir 1 pieza de 1 lado o las dos a la vez».) En los datos, la pieza
 * lleva su sitio de la izquierda y, si van sueltas, un `der` con el de la
 * derecha; los dos se guardan como si fueran del lado izquierdo.
 *
 * TODO SE HACE CON EL DEDO, Y EL COCHE SE PUEDE GIRAR SIEMPRE. Hubo dos botones
 * («Mover la pieza» y «Girar el coche»), flechas y botones de grados, y Mou los
 * quitó el mismo día: «que no aparezca lo de girar el coche porque se puede
 * girar siempre» y «tampoco hace falta lo de mover la pieza con grados y con
 * flechas, que se haga todo con el dedo y ya». Queda así:
 *   · un toque sobre una pieza la elige (sale su marco);
 *   · arrastrar la pieza elegida la mueve;
 *   · con dos dedos, girándolos, se gira la pieza elegida; si en vez de girar
 *     se abren o se cierran, es el zoom del coche, como siempre;
 *   · arrastrar por cualquier otro sitio —también sobre una pieza que no es la
 *     elegida— gira el coche. Así girar no descoloca nada sin querer.
 * Con ratón se arrastra cualquier pieza directamente, y con el teclado se
 * afina: flechas para moverla y Q y E para girarla.
 */
import * as THREE from './moa-3d-three.module.js';

const raiz = document.querySelector('[data-moa-visor]');
const $ = s => document.querySelector(s);
const grados = Math.PI / 180;
const ZONAS = { lateral: 'el costado', superior: 'capó, techo o maletero', delantera: 'el frontal', trasera: 'la trasera' };
const LADOS = { izq: 'lado izquierdo, el del conductor', der: 'lado derecho, el del acompañante' };
const espera = ms => new Promise(r => setTimeout(r, ms));

let V = null, lienzo3d = null, sucio = false, agarre = null, toque = null;
// ESTA HERRAMIENTA VIVE EN DOS SITIOS. En `colocar/` está siempre encendida. Dentro de «Personaliza tu coche»
// —la página de la tienda, y solo en un tema SIN publicar— está apagada hasta que se pulsa «Editar ubicación del
// kit» (Mou, 2026-10-01: «tiene que aparecer un botón arriba del 3D cuando selecciono un kit»). Al cliente no le
// llega: la sección del tema no manda ni el marcado ni este guion cuando el tema es el publicado.
const enWeb = !!$('.co-editar');
let activo = !enWeb;
// Guardar de verdad solo se puede con el servidor de este ordenador. Desde la tienda no hay dónde escribir:
// la colocación se copia, se pega en el chat y desde allí se deja fija en el repositorio.
const conServidor = !enWeb && (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) || new URLSearchParams(location.search).has('llave'));
const aviso = $('.co-aviso'), ficha = $('.co-ficha'), lista = $('.co-piezas'), pista = $('.co-pista');
const botonEsta = $('.co-esta'), botonesLado = [...document.querySelectorAll('.co-lados button')];
// La barra de giro: sale justo debajo del coche al elegir una pieza (Mou, 2026-10-01: «me tiene que aparecer el
// desplazador que me permita girarlo pero justo debajo del coche, que no tenga que bajar hasta abajo del todo»).
// Hacia la derecha gira a favor del reloj tal como se ve; en la copia del lado derecho, que va en espejo, se invierte.
const cajaGiro = $('.co-giro'), giroBarra = $('.co-giro input');
let giroAlReves = true;
// Los dedos que hay ahora mismo sobre el coche y, si son dos, el giro de pieza que llevan entre manos.
const dedos = new Map();
let giro = null;

const piezas = () => V.estado.piezas;
const elegida = () => V.estado.marco;
// El sitio que se toca ahora: el de las dos si van enlazadas, o el del lado elegido si van sueltas.
const destino = (p, lado) => (p.der && lado === 'der') ? p.der : p;
const destinoElegido = () => { const p = elegida(); return p ? destino(p, V.estado.marcoLado) : null; };

/* ---- qué hay bajo el dedo: las pieles que se tocan en ese punto, con el sitio de su lienzo ---- */
const rayo = new THREE.Raycaster(), puntero = new THREE.Vector2();
function toques(clientX, clientY) {
  const r = lienzo3d.getBoundingClientRect();
  puntero.set((clientX - r.left) / r.width * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
  rayo.setFromCamera(puntero, V.camara);
  const todos = rayo.intersectObject(V.estado.coche, true).filter(h => h.object !== V.estado.franja);
  const salida = [];
  for (const h of todos) {
    if (h.distance > todos[0].distance + 0.008) break;       // solo lo que está en la superficie, no lo de detrás
    const clave = Object.keys(V.pieles).find(k => V.pieles[k].malla === h.object);
    if (!clave || !h.uv || salida.some(s => s.clave === clave)) continue;
    const piel = V.pieles[clave], n = h.face.normal.clone().transformDirection(h.object.matrixWorld);
    salida.push({ clave, zona: piel.zona, lado: piel.lado, x: h.uv.x * piel.tela.width, y: h.uv.y * piel.tela.height,
      nota: { lateral: Math.abs(n.x), superior: n.y, delantera: n.z, trasera: -n.z }[piel.zona] });
  }
  return salida;
}
// La piel que mejor mira al dedo. La pieza no cambia de zona por una diferencia pequeña: si no, tiembla en el hombro.
function mejor(candidatas, actual) {
  const m = candidatas.reduce((a, b) => (b.nota > a.nota ? b : a));
  const a = candidatas.find(c => c.zona === actual);
  return a && a.nota >= m.nota - 0.3 ? a : m;
}
// El punto del lienzo, llevado al lado izquierdo si lo que se toca es la pieza de la derecha.
const alIzquierdo = (t, lado) => (lado === 'der' && V.defs()[t.zona].espejo !== 'uv') ? V.reflejo(t.zona, t.x, t.y) : [t.x, t.y];

function enLaPieza(p, s, x, y) {
  const [cx, cy] = V.aPx(s.zona, s.centro_m), t = -s.giro_grados * grados, dx = x - cx, dy = y - cy, ppm = V.pxPorMetro();
  const lx = dx * Math.cos(t) + dy * Math.sin(t), ly = -dx * Math.sin(t) + dy * Math.cos(t);
  const w = p.ancho_m * ppm, h = p.alto_m * ppm;
  if (Math.abs(lx) > w / 2 || Math.abs(ly) > h / 2) return 0;
  const ax = Math.min(p.alfa.width - 1, Math.floor((lx / w + 0.5) * p.alfa.width));
  const ay = Math.min(p.alfa.height - 1, Math.floor((ly / h + 0.5) * p.alfa.height));
  return p.alfa.data[(ay * p.alfa.width + ax) * 4 + 3] > 30 ? 2 : 1;       // 2: sobre el dibujo · 1: dentro de su caja
}
// La pieza que hay bajo ese toque y de qué lado es.
function piezaEn(t) {
  let hallada = null;
  piezas().forEach(p => ['izq', 'der'].forEach(lado => {
    const s = V.caraDe(p, lado);
    if (s.zona !== t.zona || (t.lado && t.lado !== lado)) return;
    const punto = alIzquierdo(t, lado), v = enLaPieza(p, s, punto[0], punto[1]);
    if (v && (!hallada || v > hallada.v || (v === hallada.v && p.ancho_m * p.alto_m < hallada.p.ancho_m * hallada.p.alto_m))) hallada = { p, lado, v };
  }));
  return hallada;
}
function piezaBajo(clientX, clientY) {
  let hallada = null, donde = null;
  for (const t of toques(clientX, clientY)) {
    const h = piezaEn(t);
    if (h && (!hallada || h.v > hallada.v)) { hallada = h; donde = t; }
  }
  return hallada ? { ...hallada, donde } : null;
}

/* ---- dónde está la pieza en la pantalla: hace falta para que las flechas muevan hacia donde apuntan ---- */
// De un punto del lienzo de una piel a su sitio en el espacio: se busca el triángulo de la malla que lo contiene.
function enElMundo(clave, x, y) {
  const piel = V.pieles[clave];
  if (!piel) return null;
  const g = piel.malla.geometry, uv = g.attributes.uv, pos = g.attributes.position, idx = g.index;
  const u = x / piel.tela.width, v = y / piel.tela.height, n = idx ? idx.count : pos.count;
  for (let i = 0; i < n; i += 3) {
    const a = idx ? idx.getX(i) : i, b = idx ? idx.getX(i + 1) : i + 1, c = idx ? idx.getX(i + 2) : i + 2;
    const ax = uv.getX(a), ay = uv.getY(a), bx = uv.getX(b) - ax, by = uv.getY(b) - ay, cx = uv.getX(c) - ax, cy = uv.getY(c) - ay;
    const det = bx * cy - cx * by;
    if (Math.abs(det) < 1e-12) continue;
    const px = u - ax, py = v - ay, w1 = (px * cy - cx * py) / det, w2 = (bx * py - px * by) / det;
    if (w1 < -1e-4 || w2 < -1e-4 || w1 + w2 > 1 + 1e-4) continue;
    const A = new THREE.Vector3().fromBufferAttribute(pos, a), AB = new THREE.Vector3().fromBufferAttribute(pos, b).sub(A),
      AC = new THREE.Vector3().fromBufferAttribute(pos, c).sub(A);
    piel.malla.updateWorldMatrix(true, false);
    return { punto: A.clone().addScaledVector(AB, w1).addScaledVector(AC, w2).applyMatrix4(piel.malla.matrixWorld),
      normal: AB.clone().cross(AC).normalize().transformDirection(piel.malla.matrixWorld) };
  }
  return null;
}
// De las dos copias de la pieza, la que se está viendo: la que mira a la cámara.
function vista(p) {
  const lados = V.estado.marcoLado === 'ambos' ? ['izq', 'der'] : [V.estado.marcoLado];
  let buena = null;
  for (const lado of lados) {
    const s = V.caraDe(p, lado);
    let c = V.aPx(s.zona, s.centro_m);
    if (lado === 'der' && V.defs()[s.zona].espejo !== 'uv') c = V.reflejo(s.zona, c[0], c[1]);
    // Si el centro de la pieza cae donde no hay chapa (un paso de rueda, un cristal), vale un punto de chapa cercano:
    // solo se usa para saber hacia dónde es «arriba» o «a la derecha» en la pantalla, y eso cambia poco en un palmo.
    const clave = s.zona === 'lateral' ? 'lateral_' + lado : s.zona, ppm = V.pxPorMetro();
    let m = enElMundo(clave, c[0], c[1]);
    for (const radio of [0.05, 0.1, 0.18, 0.28]) {
      for (let k = 0; k < 8 && !m; k++) m = enElMundo(clave, c[0] + Math.cos(k * Math.PI / 4) * radio * ppm, c[1] + Math.sin(k * Math.PI / 4) * radio * ppm);
      if (m) break;
    }
    if (!m) continue;
    const mira = m.normal.dot(V.camara.position.clone().sub(m.punto).normalize());
    if (!buena || mira > buena.mira) buena = { lado, punto: m.punto, mira };
  }
  return buena;
}

/* ---- panel ---- */
function movida(p) {
  const m = p.del_montaje;
  return !!p.der || p.zona !== 'lateral' || p.giro_grados !== m.giro_grados || p.centro_m[0] !== m.centro_m[0] || p.centro_m[1] !== m.centro_m[1];
}
function refrescar() {
  lista.querySelectorAll('.co-pieza').forEach((b, i) => {
    b.setAttribute('aria-pressed', String(piezas()[i] === elegida()));
    b.dataset.movida = String(movida(piezas()[i]));
  });
  const p = elegida(), s = destinoElegido(), lado = V.estado.marcoLado;
  botonEsta.disabled = !p;
  botonesLado.forEach(b => { b.disabled = !p; b.setAttribute('aria-pressed', String(!!p && b.dataset.lado === lado)); });
  if (cajaGiro) {
    cajaGiro.hidden = !p || !activo;
    if (p) {
      giroAlReves = (vista(p) || {}).lado !== 'der';
      giroBarra.value = Math.round(giroAlReves ? (360 - s.giro_grados) % 360 : s.giro_grados);
    }
  }
  pista.textContent = !p ? 'Toca una pieza en el coche, o en la lista de abajo, para elegirla.'
    : 'Arrástrala para moverla y gírala con la barra. Fuera de ella, el dedo gira el coche.';
  if (!p) { ficha.textContent = ''; return; }
  ficha.textContent = p.nombre + ' · ' + Math.round(Math.max(p.ancho_m, p.alto_m) * 100) + ' × ' + Math.round(Math.min(p.ancho_m, p.alto_m) * 100) +
    ' cm · ' + (lado === 'ambos' ? 'los dos lados a la vez' : 'solo el ' + LADOS[lado]) + ' · está en ' + ZONAS[s.zona] + '.';
}
// Elegir una pieza. Si va suelta, se elige la del lado que se ha tocado; si va enlazada, las dos.
function elegir(p, lado, parpadea = true) {
  V.estado.marco = p;
  V.estado.marcoLado = p && p.der ? (lado === 'der' ? 'der' : 'izq') : 'ambos';
  refrescar();
  V.pintarPieles();
  parpadear(!!p && parpadea);
}
// LA PIEZA RECIÉN ELEGIDA PARPADEA SUAVE HASTA QUE SE TOCA (Mou, 2026-10-01: «cuando seleccione una pieza, esta
// parpadee levemente hasta que la toque»). Sirve para encontrarla en el coche cuando se elige en la lista.
// Va y viene entre el 45 % y el 100 % de opacidad, una vez por segundo. Se repinta cada 80 ms y solo la zona
// donde está la pieza: repintar las cinco pieles a cada cuadro era gastar el móvil para nada.
let parpadeo = 0;
function pararParpadeo() { clearInterval(parpadeo); parpadeo = 0; V.estado.pulso = null; }
function parpadear(si) {
  const habia = V.estado.pulso != null;
  pararParpadeo();
  const p = elegida();
  if (!si || !p) { if (habia) V.pintarPieles(); return; }
  const t0 = performance.now();
  parpadeo = setInterval(() => {
    if (elegida() !== p) { pararParpadeo(); V.pintarPieles(); return; }
    V.estado.pulso = 0.725 + 0.275 * Math.cos((performance.now() - t0) / 1000 * 2 * Math.PI);
    V.pintarPieles([...new Set(['izq', 'der'].map(l => V.caraDe(p, l).zona))]);
  }, 80);
}
// Sin servidor (la tienda) no hay dónde guardar: ni se avisa de cambios pendientes ni se frena al salir de la página.
function tocada(zonas) { pararParpadeo(); sucio = conServidor; aviso.textContent = conServidor ? 'Hay cambios sin guardar.' : ''; refrescar(); V.pintarPieles(zonas); }
function redondear(c) { return c.map(v => Math.round(v * 10000) / 10000); }
function girar(a) { const s = destinoElegido(); if (!s) return; s.giro_grados = ((Math.round(a) % 360) + 360) % 360; tocada(); }
// Girar un poco. En la copia del lado derecho el dibujo va en espejo, así que el sentido se invierte para que
// «a favor del reloj» sea a favor del reloj en lo que se está viendo.
function girarPaso(delta) {
  const p = elegida(), s = destinoElegido();
  if (!s) return;
  const v = vista(p);
  girar(s.giro_grados + delta * (v && v.lado === 'der' ? -1 : 1));
}
// Mover un poco hacia donde apunta la flecha EN LA PANTALLA, no en el lienzo de la piel: según el lado del coche
// y cómo esté girado, «a la derecha» del lienzo puede ser cualquier cosa. Se mira qué punto de la chapa hay un
// poco más allá en esa dirección y se avanza medio centímetro (o dos) hacia él.
function empujar(dx, dy, grande) {
  const p = elegida();
  if (!p) return;
  const ppm = V.pxPorMetro(), paso = (grande ? 0.02 : 0.005) * ppm, v = vista(p);
  const s = destino(p, v ? v.lado : V.estado.marcoLado), c = V.aPx(s.zona, s.centro_m);
  let hecho = false;
  if (v) {
    const r = lienzo3d.getBoundingClientRect(), q = v.punto.clone().project(V.camara);
    const sx = r.left + (q.x + 1) / 2 * r.width, sy = r.top + (1 - q.y) / 2 * r.height;
    const suyo = t => t.zona === s.zona && (!t.lado || t.lado === v.lado);
    // Se mira 5 px más allá, no más: en una chapa curva, un tramo largo no lleva la misma dirección que el primer paso
    // (con 14 px, «arriba» se iba 17 grados hacia un lado en la aleta).
    const aqui = toques(sx, sy).find(suyo), alla = toques(sx + dx * 5, sy + dy * 5).find(suyo);
    if (aqui && alla) {
      const a = alIzquierdo(aqui, v.lado), b = alIzquierdo(alla, v.lado), largo = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (largo > 0.5) { c[0] += (b[0] - a[0]) / largo * paso; c[1] += (b[1] - a[1]) / largo * paso; hecho = true; }
    }
  }
  if (!hecho) { c[0] += dx * paso; c[1] += dy * paso; }       // la pieza no se ve desde aquí: se mueve en su lienzo
  s.centro_m = redondear(V.aMetros(s.zona, c[0], c[1]));
  tocada([s.zona]);
}
function alMontaje(p) { p.der = null; p.zona = 'lateral'; p.centro_m = p.del_montaje.centro_m.slice(); p.giro_grados = p.del_montaje.giro_grados; }
// «Los dos a la vez» o «solo el izquierdo / solo el derecho».
function modo(m) {
  const p = elegida();
  if (!p) return;
  const antes = !!p.der;
  if (m === 'ambos') {
    if (p.der) {                                               // las dos pasan a ir donde está la del lado elegido
      if (V.estado.marcoLado === 'der') { p.zona = p.der.zona; p.centro_m = p.der.centro_m; p.giro_grados = p.der.giro_grados; }
      p.der = null;
    }
  } else if (!p.der) {
    p.der = { zona: p.zona, centro_m: p.centro_m.slice(), giro_grados: p.giro_grados };      // la derecha arranca donde estaba
  }
  V.estado.marcoLado = m;
  if (antes !== !!p.der) tocada(); else { refrescar(); V.pintarPieles(); }
}

// Dentro de la página de la tienda: qué se enseña según se esté colocando o no.
function pintarBarra() {
  if (!enWeb) return;
  $('.co-editar').hidden = activo || !V.estado.kit;
  $('.co-barra').hidden = !activo;
  $('.co-caja').hidden = !activo;
  raiz.classList.toggle('pt--colocando', activo);
  velo();
}

// El aviso del primer kit. Mou, 2026-10-01: «cuando seleccionemos el primer kit, que aparezca un mensaje en medio del
// coche oscureciendo un poco toda la imagen del coche con una alerta que enseñe cómo mover los kits». Sale una vez
// por visita, al aparecer el primer kit, y solo donde existe el botón de editar, que queda a la vista por encima
// del velo para que se vea dónde está. Se quita con «Entendido», tocando lo oscuro, al entrar a editar o al quitar el kit.
let ensenado = false;
function velo() {
  const puesto = $('.co-velo');
  if (puesto && (activo || !V.estado.kit)) puesto.remove();
  if (ensenado || activo || !V.estado.kit) return;
  ensenado = true;
  const v = document.createElement('div'), caja = document.createElement('div');
  v.className = 'co-velo'; v.setAttribute('role', 'dialog'); v.setAttribute('aria-label', 'Cómo mover el kit');
  caja.className = 'co-velo-caja';
  const titulo = document.createElement('p'), texto = document.createElement('p'), ok = document.createElement('button');
  titulo.className = 'co-velo-titulo'; titulo.textContent = 'Puedes mover el kit';
  texto.className = 'co-velo-texto';
  texto.textContent = 'Pulsa «Editar ubicación del kit», aquí abajo. Toca una pieza y arrástrala con el dedo; para girarla, usa la barra que sale debajo del coche.';
  ok.type = 'button'; ok.className = 'co-velo-ok'; ok.textContent = 'Entendido';
  caja.append(titulo, texto, ok);
  v.appendChild(caja);
  v.addEventListener('click', () => v.remove());               // no hay recuadro: cualquier toque sobre el coche oscurecido lo quita
  $('.co-editar').parentElement.appendChild(v);
}
function editar(si) {
  activo = !!si && !!V.estado.kit;
  // El gesto táctil del lienzo no se toca aquí: lo fija OrbitControls para todo el visor, colocando o no.
  if (!activo) { agarre = null; giro = null; dedos.clear(); V.controles.enabled = true; }
  elegir(null);
  pintarBarra();
}

// La lista de piezas se rehace cada vez que el visor cambia de kit.
function listar() {
  lista.textContent = '';
  V.estado.marco = null;
  V.estado.marcoLado = 'ambos';
  $('.co-kit').textContent = V.estado.kit ? V.estado.kit.nombre : 'ninguno: elige un kit más abajo';
  piezas().forEach(p => {
    if (!p.alfa) {                                             // el recorte de la pieza, para saber si el dedo cae sobre su dibujo
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(p.img.naturalWidth / 2)); c.height = Math.max(1, Math.round(p.img.naturalHeight / 2));
      const cc = c.getContext('2d', { willReadFrequently: true });
      cc.drawImage(p.img, 0, 0, c.width, c.height);
      p.alfa = cc.getImageData(0, 0, c.width, c.height);
    }
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'co-pieza'; b.setAttribute('aria-pressed', 'false');
    const mini = document.createElement('img'); mini.src = p.img.src; mini.alt = '';
    b.append(mini, document.createTextNode(p.nombre));
    b.addEventListener('click', () => elegir(p, 'izq'));
    lista.appendChild(b);
  });
  refrescar();
  if (enWeb) { if (activo && !V.estado.kit) editar(false); else pintarBarra(); }      // sin kit no hay nada que colocar
}

/* ---- guardar ---- */
const sitio = s => ({ zona: s.zona, centro_m: s.centro_m, giro_grados: s.giro_grados });
async function guardar() {
  if (!V.estado.kit) { aviso.textContent = 'Elige primero un kit.'; return false; }
  if (!conServidor) return true;                                // en la tienda no se guarda nada
  const boton = $('.co-guardar');
  boton.disabled = true;
  aviso.textContent = 'Guardando…';
  try {
    // La llave solo hace falta cuando el servidor se ha abierto a la wifi para usar esto desde el teléfono.
    const r = await fetch('/guardar-colocacion', { method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Llave': new URLSearchParams(location.search).get('llave') || '' },
      body: JSON.stringify({ kit: V.estado.kit.id, coche: V.estado.cocheId, piezas: piezas().map(p => ({ ...sitio(p), der: p.der ? sitio(p.der) : null })) }) });
    const j = await r.json().catch(() => ({ detalle: 'el servidor no ha contestado como se esperaba' }));
    if (!r.ok || !j.ok) throw new Error(j.detalle || ('error ' + r.status));
    sucio = false;
    aviso.textContent = 'Guardado: ' + j.piezas + ' piezas. El visor del cliente ya enseña el kit así.';
  } catch (err) {
    aviso.textContent = 'No se ha guardado. ¿Está arrancado el servidor de colocación? (' + String(err.message).slice(0, 160) + ')';
  }
  boton.disabled = false;
  return !sucio;
}

/* ---- dos dedos: girar la pieza elegida ---- */
// Al posar el segundo dedo no se sabe todavía si se quiere girar la pieza o hacer zoom al coche. Se decide por lo
// primero que pase: si los dedos giran más de 8 grados, es giro de pieza (y el zoom se para); si antes se separan
// o se juntan más de un 12 %, es zoom y la pieza no se toca. Sin eso, cada zoom dejaba la pieza un poco torcida.
function empezarGiro() {
  agarre = null;                                                // el primer dedo estaba arrastrando: se suelta
  toque = null;
  V.controles.enabled = true;
  const s = destinoElegido(), [a, b] = [...dedos.values()];
  giro = s ? { s, angulo: Math.atan2(b[1] - a[1], b[0] - a[0]), largo: Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, grados: s.giro_grados,
    modo: '', resto: 0, sentido: (vista(elegida()) || {}).lado === 'der' ? -1 : 1 } : null;       // la copia derecha va en espejo
}
function girando() {
  const [a, b] = [...dedos.values()], largo = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const vuelta = (((Math.atan2(b[1] - a[1], b[0] - a[0]) - giro.angulo) / grados + 540) % 360) - 180;      // por el camino corto
  if (!giro.modo) {
    if (Math.abs(vuelta) > 8) { giro.modo = 'girar'; giro.resto = vuelta; V.controles.enabled = false; }
    else if (Math.abs(largo / giro.largo - 1) > 0.12) giro.modo = 'zoom';
  }
  if (giro.modo !== 'girar') return;
  // En pantalla el ángulo crece a favor del reloj; el giro de la pieza se cuenta al revés.
  giro.s.giro_grados = Math.round(((((giro.grados - (vuelta - giro.resto) * giro.sentido) % 360) + 360) % 360) * 10) / 10;
  tocada([giro.s.zona]);
}

/* ---- arranque: cuando el visor está listo, se le añade el arrastre ---- */
(async function () {
  while (!(window.MoaVisor && window.MoaVisor.estado.listo)) await espera(60);
  V = window.MoaVisor;
  lienzo3d = V.lienzo;
  V.estado.alCambiarKit = listar;
  if (!enWeb && !V.estado.kit) { const primero = raiz.querySelector('.pv-kit:not([data-id=""])'); if (primero) primero.click(); }
  if (enWeb) {
    $('.co-editar').addEventListener('click', () => editar(true));
    $('.co-listo').addEventListener('click', () => editar(false));
    // Mou, 2026-10-01: «quita lo de copiar colocación»; y el 2026-10-02: «te he dicho que elimines lo de guardar
    // colocación». En la página del cliente (la tienda y la maqueta `web/`) no hay botón de guardar ni de copiar:
    // lo que se mueve dura lo que dura la visita. Guardar solo existe en la herramienta interna `colocar/`.
  }
  listar();

  lienzo3d.addEventListener('pointerdown', e => {
    if (!activo) return;                                        // sin pulsar «Editar ubicación», el visor es el del cliente
    dedos.set(e.pointerId, [e.clientX, e.clientY]);
    if (dedos.size === 2) { empezarGiro(); return; }            // el segundo dedo: girar la pieza o hacer zoom, no arrastrar
    const hallada = piezaBajo(e.clientX, e.clientY);
    toque = { x: e.clientX, y: e.clientY, cuando: performance.now(), hallada };
    // Fuera de una pieza, el gesto gira el coche: SIEMPRE se puede girar.
    // Con el dedo solo se arrastra la pieza que ya está elegida; sobre otra, un toque la elige (al soltar) y
    // arrastrar gira, para que girar el coche no descoloque nada sin querer. Con ratón se arrastra cualquiera.
    if (!hallada || (e.pointerType !== 'mouse' && hallada.p !== elegida())) return;
    const punto = alIzquierdo(hallada.donde, hallada.lado), s = V.caraDe(hallada.p, hallada.lado), c = V.aPx(s.zona, s.centro_m);
    agarre = { p: hallada.p, lado: hallada.lado, dx: c[0] - punto[0], dy: c[1] - punto[1] };
    elegir(hallada.p, hallada.lado, false);                     // se está tocando: deja de parpadear
    V.controles.enabled = false;
    try { lienzo3d.setPointerCapture(e.pointerId); } catch (_) { /* sin captura también se arrastra, mientras el dedo siga sobre el lienzo */ }
  }, true);
  lienzo3d.addEventListener('pointermove', e => {
    if (dedos.has(e.pointerId)) dedos.set(e.pointerId, [e.clientX, e.clientY]);
    if (giro && dedos.size === 2) { girando(); return; }
    if (!agarre) return;
    // en el costado, la pieza no salta al otro lado del coche: solo valen los toques de su lado
    const candidatas = toques(e.clientX, e.clientY).filter(t => !t.lado || t.lado === agarre.lado);
    if (!candidatas.length) return;                             // el dedo se ha salido de la chapa: la pieza espera
    const s = destino(agarre.p, agarre.lado), t = mejor(candidatas, s.zona), antes = s.zona;
    const punto = alIzquierdo(t, agarre.lado);
    if (antes !== t.zona) { agarre.dx = 0; agarre.dy = 0; }     // al cambiar de zona la distancia guardada ya no vale
    s.zona = t.zona;
    s.centro_m = redondear(V.aMetros(t.zona, punto[0] + agarre.dx, punto[1] + agarre.dy));
    tocada(antes === t.zona ? [t.zona] : [antes, t.zona]);
  });
  const soltar = e => {
    if (e) dedos.delete(e.pointerId);
    if (dedos.size < 2 && (giro || !V.controles.enabled && !agarre)) { giro = null; V.controles.enabled = true; }
    const habia = !!agarre;
    if (agarre) { agarre = null; V.controles.enabled = true; }
    // Un toque corto sobre una pieza, sin arrastrar, la elige: es como se empieza en el móvil.
    if (e && e.type === 'pointerup' && toque && !habia && toque.hallada && performance.now() - toque.cuando < 500 &&
        Math.hypot(e.clientX - toque.x, e.clientY - toque.y) < 8) elegir(toque.hallada.p, toque.hallada.lado);
    toque = null;
  };
  lienzo3d.addEventListener('pointerup', soltar);
  lienzo3d.addEventListener('pointercancel', soltar);

  botonesLado.forEach(b => b.addEventListener('click', () => modo(b.dataset.lado)));
  if (giroBarra) giroBarra.addEventListener('input', () => girar(giroAlReves ? 360 - Number(giroBarra.value) : Number(giroBarra.value)));
  botonEsta.addEventListener('click', () => { const p = elegida(); if (p) { alMontaje(p); V.estado.marcoLado = 'ambos'; tocada(); } });
  $('.co-todas').addEventListener('click', () => { piezas().forEach(alMontaje); V.estado.marcoLado = 'ambos'; tocada(); });
  if ($('.co-guardar')) $('.co-guardar').addEventListener('click', guardar);   // solo está en la herramienta interna
  window.addEventListener('beforeunload', e => { if (sucio) { e.preventDefault(); e.returnValue = ''; } });

  window.addEventListener('keydown', e => {
    if (!activo || !elegida() || /INPUT|TEXTAREA/.test(e.target.tagName)) return;
    const hecho = {
      ArrowLeft: () => empujar(-1, 0, e.shiftKey), ArrowRight: () => empujar(1, 0, e.shiftKey),
      ArrowUp: () => empujar(0, -1, e.shiftKey), ArrowDown: () => empujar(0, 1, e.shiftKey),
      q: () => girarPaso(e.shiftKey ? 5 : 1), e: () => girarPaso(e.shiftKey ? -5 : -1),
      Escape: () => elegir(null)
    }[e.key.length === 1 ? e.key.toLowerCase() : e.key];
    if (!hecho) return;
    e.preventDefault();
    hecho();
  });

  window.MoaColocar = { toques, piezaEn, piezaBajo, mejor, elegir, modo, guardar, piezas, vista, empujar, girarPaso, editar,
    sucio: () => sucio, activo: () => activo, parpadea: () => !!parpadeo };
})();
